import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton, Pagination, SearchInput } from "@/hq/HQPrimitives";

export default function HQWebsites() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    const params = { page, page_size: 25 };
    if (q) params.q = q;
    hqApi.get("/websites", { params })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr("Something went wrong loading websites."));
    return () => { alive = false; };
  }, [q, page]);

  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="hq-websites">
      <Card className="p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search domain / URL…" testId="hq-websites-search" />
          <div className="ml-auto text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A]" data-testid="hq-websites-total">
            {data ? `${data.total} website${data.total === 1 ? "" : "s"}` : "…"}
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {!data ? (
          <div className="p-6 space-y-3">{[0,1,2].map(i => <Skeleton key={i} h={40} />)}</div>
        ) : items.length === 0 ? (
          <EmptyState title="No websites yet" hint="Websites linked to businesses will appear here." testId="hq-websites-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">
                <tr>
                  <th className="text-left px-5 py-3 font-bold">Domain</th>
                  <th className="text-left px-5 py-3 font-bold">Business</th>
                  <th className="text-left px-5 py-3 font-bold">Raw URL</th>
                  <th className="text-left px-5 py-3 font-bold">Primary</th>
                  <th className="text-left px-5 py-3 font-bold">Platform</th>
                  <th className="text-left px-5 py-3 font-bold">Status</th>
                  <th className="text-left px-5 py-3 font-bold">Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((w) => (
                  <tr key={w.id} className="border-t border-[#EAEAEA] hover:bg-[#FFFDF6]" data-testid={`hq-website-row-${w.id}`}>
                    <td className="px-5 py-3 font-bold break-all">{w.normalized_domain}</td>
                    <td className="px-5 py-3 text-[#4A4A4A]">
                      {w.business ? (
                        <Link to={`/hq/businesses/${w.business.id}`} className="hover:text-[#FF6A00]">{w.business.name}</Link>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-3 text-[13px] text-[#4A4A4A] break-all">{w.url || "—"}</td>
                    <td className="px-5 py-3 text-[11px] tracking-[0.14em] uppercase text-[#8A8A8A]">{w.primary_website ? "yes" : ""}</td>
                    <td className="px-5 py-3 text-[13px] text-[#4A4A4A]">{w.platform || "—"}</td>
                    <td className="px-5 py-3 text-[11px] tracking-[0.14em] uppercase text-[#8A8A8A]">{w.status || "—"}</td>
                    <td className="px-5 py-3 text-[12px] text-[#8A8A8A]">
                      {w.created_at ? new Date(w.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data && <Pagination page={data.page} total_pages={data.total_pages} onPage={setPage} testIdPrefix="hq-websites-page" />}
    </div>
  );
}
